import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon, Menu, X } from "lucide-react";

const LINKS = [
  { to: "/product", label: "Product" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/learn", label: "Learn" },
  { to: "/security", label: "Security" },
];

export function MarketingNav() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 glass border-b border-[var(--border2)]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.replace(/\s/g, "-").toLowerCase()}`}
              className="text-sm text-[var(--text-sec)] hover:text-[var(--text)] transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={toggle} aria-label="Toggle theme" data-testid="theme-toggle"
            className="p-2 rounded-lg text-[var(--text-sec)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition-colors">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {user ? (
            <button onClick={() => nav("/app")} data-testid="nav-dashboard"
              className="btn-gold rounded-full px-4 py-2 text-sm font-semibold transition-all hover:brightness-110">
              Open Terminal
            </button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login"
                className="hidden sm:inline text-sm text-[var(--text-sec)] hover:text-[var(--text)] px-3 py-2 transition-colors">
                Log in
              </Link>
              <Link to="/signup" data-testid="nav-signup"
                className="btn-gold rounded-full px-4 py-2 text-sm font-semibold transition-all hover:brightness-110">
                Start Paper Trading
              </Link>
            </>
          )}
          <button className="md:hidden p-2 text-[var(--text)]" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-[var(--border2)] px-4 py-3 space-y-1 bg-[var(--bg-elev)]">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text)]">{l.label}</Link>
          ))}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-[var(--border2)] bg-[var(--bg-elev)]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-8 md:grid-cols-4">
          <div className="md:col-span-2">
            <Logo />
            <p className="mt-4 text-sm text-[var(--text-sec)] max-w-sm">
              Your strategy. Monitored continuously. Aurum AI watches XAU/USD against your
              trading plan, calculates risk before action, and enforces discipline.
            </p>
            <p className="mt-4 text-xs text-[var(--text-muted)] max-w-md">
              Trading involves substantial risk of loss. Aurum AI does not predict the future
              and does not provide financial advice.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-3">Product</div>
            <div className="space-y-2 text-sm text-[var(--text-sec)]">
              <Link className="block hover:text-[var(--text)]" to="/product">Overview</Link>
              <Link className="block hover:text-[var(--text)]" to="/how-it-works">How It Works</Link>
              <Link className="block hover:text-[var(--text)]" to="/pricing">Pricing</Link>
              <Link className="block hover:text-[var(--text)]" to="/learn">Learning Center</Link>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-3">Company</div>
            <div className="space-y-2 text-sm text-[var(--text-sec)]">
              <Link className="block hover:text-[var(--text)]" to="/about">About</Link>
              <Link className="block hover:text-[var(--text)]" to="/security">Security</Link>
              <Link className="block hover:text-[var(--text)]" to="/login">Log in</Link>
            </div>
            <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-3 mt-6">Legal</div>
            <div className="space-y-2 text-sm text-[var(--text-sec)]">
              <Link className="block hover:text-[var(--text)]" to="/terms">Terms of Service</Link>
              <Link className="block hover:text-[var(--text)]" to="/privacy">Privacy Policy</Link>
              <Link className="block hover:text-[var(--text)]" to="/refunds">Refund Policy</Link>
            </div>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-[var(--border2)] text-xs text-[var(--text-muted)]">
          © {new Date().getFullYear()} Aurum AI. All analysis is educational. Not investment advice.
        </div>
      </div>
    </footer>
  );
}
